import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import MobileTopBar from "@/components/MobileTopBar";
import ResearchPage from "@/pages/ResearchPage";
import RunsPage from "@/pages/RunsPage";
import ArticlePage from "@/pages/ArticlePage";
import LibraryPage from "@/pages/LibraryPage";
import StockLibraryPage from "@/pages/StockLibraryPage";
import ContentCalendarPage from "@/pages/ContentCalendarPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import NewsletterPage from "@/pages/NewsletterPage";
import PrintEditionPage from "@/pages/PrintEditionPage";
import DigitalEditionsPage from "@/pages/DigitalEditionsPage";
import EditionPage from "@/pages/EditionPage";
import SettingsPage from "@/pages/SettingsPage";
import CustomArticlePage from "@/pages/CustomArticlePage";
import UploadArticlePage from "@/pages/UploadArticlePage";
import SocialCommentsPage from "@/pages/SocialCommentsPage";
import ProspectsPage from "@/pages/ProspectsPage";
import TelegramTipsPage from "@/pages/TelegramTipsPage";
import PromptHistoryPage from "@/pages/PromptHistoryPage";
import ScheduledTasksPage from "@/pages/ScheduledTasksPage";
import AdminPage from "@/pages/AdminPage";
import LlmModelsPage from "@/pages/LlmModelsPage";
import LlmCostPage from "@/pages/LlmCostPage";
import RequestLogPage from "@/pages/RequestLogPage";
import LoginPage from "@/pages/LoginPage";
import UploadJsonPage from "@/pages/UploadJsonPage";
import SponsorAdsPage from "@/pages/SponsorAdsPage";
import PublicAdReportPage from "@/pages/PublicAdReportPage";
import PublicStoryReviewPage from "@/pages/PublicStoryReviewPage";
import { AuthProvider, useAuth } from "@/lib/auth";
import { PromptOnlyProvider } from "@/lib/promptOnly";

function Shell() {
  const [navOpen, setNavOpen] = useState(false);
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[var(--bg)]">
      <Sidebar isOpen={navOpen} onClose={() => setNavOpen(false)} />
      <main className="flex-1 overflow-hidden flex flex-col min-w-0">
        <MobileTopBar onOpen={() => setNavOpen(true)} />
        <div className="flex-1 overflow-hidden flex flex-col min-w-0">
          <Routes>
            {/* Newsdesk opens on the Library (2026-09-28). */}
            <Route path="/" element={<Navigate to="/library" replace />} />
            <Route path="/research" element={<ResearchPage />} />
            <Route path="/runs" element={<RunsPage />} />
            <Route path="/custom" element={<CustomArticlePage />} />
            <Route path="/upload" element={<UploadArticlePage />} />
            <Route path="/social-comments" element={<SocialCommentsPage />} />
            <Route path="/prospects" element={<ProspectsPage />} />
            <Route path="/telegram" element={<TelegramTipsPage />} />
            <Route path="/prompts" element={<PromptHistoryPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/stock" element={<StockLibraryPage />} />
            <Route path="/calendar" element={<ContentCalendarPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/print-edition" element={<PrintEditionPage />} />
            <Route path="/editions" element={<DigitalEditionsPage />} />
            <Route path="/editions/:id" element={<EditionPage />} />
            <Route path="/scheduled" element={<ScheduledTasksPage />} />
            <Route path="/sponsor-ads" element={<SponsorAdsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/article/:id" element={<ArticlePage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/llm-models" element={<LlmModelsPage />} />
            <Route path="/admin/cost" element={<LlmCostPage />} />
            <Route path="/admin/upload-json" element={<UploadJsonPage />} />
            <Route path="/admin/request-log" element={<RequestLogPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function AuthGate() {
  const { user } = useAuth();

  // Keep the browser tab title in sync with the signed-in property.
  // Format: "Property Name | Newsdesk" once logged in; plain "Newsdesk"
  // on the login screen. Runs on every user/property change so
  // switching properties updates the tab in real time.
  useEffect(() => {
    if (!user) {
      document.title = "Newsdesk";
      return;
    }
    const active = (user.properties || []).find(
      (p) => p.id === user.active_property_id
    );
    document.title = active?.name
      ? `${active.name} | Newsdesk`
      : "Newsdesk";
  }, [user]);

  if (user === null) {
    return (
      <div
        data-testid="auth-loading"
        className="min-h-screen w-full flex items-center justify-center bg-[var(--bg)]"
      >
        <Loader2 className="w-6 h-6 animate-spin text-[var(--ink-muted)]" />
      </div>
    );
  }
  if (!user) return <LoginPage />;
  // Prompt-only mode (Cmd/Ctrl held → save prompt, skip LLM) is admin-
  // only. Gating happens INSIDE the provider so non-admin sessions
  // don't even listen for the key events.
  return (
    <PromptOnlyProvider isAdmin={user.role === "admin"}>
      <Shell />
    </PromptOnlyProvider>
  );
}

// Everything behind the login lives under this splat route; `Shell`'s
// own <Routes> resolve as descendants of it. The one public page —
// the client-facing sponsored-post report — sits BESIDE it so it never
// mounts AuthProvider (no /auth/me call, no login redirect).
function AuthedApp() {
  return (
    <AuthProvider>
      <AuthGate />
      <Toaster
        position="bottom-right"
        // Auto-dismiss after 4s so working editors aren't buried
        // under a stack of stale toasts. Persistent record of
        // everything shown lives in the Notification Center
        // (Sidebar → Notifications) via `useSonner` -> log.
        duration={4000}
        toastOptions={{
          style: {
            background: "#0f172a",
            color: "white",
            border: "1px solid #0f172a",
            borderRadius: 0,
            fontFamily: "IBM Plex Sans, sans-serif",
          },
        }}
      />
    </AuthProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/share/ads/:token" element={<PublicAdReportPage />} />
          <Route path="/review/:token" element={<PublicStoryReviewPage />} />
          <Route path="*" element={<AuthedApp />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
